import React, { useState } from "react";

import { Modal } from "../../../common/Modal";
import {
  ShieldIcon,
  BriefcaseIcon,
  UsersIcon,
} from "../../../../assets/ui/icons";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { EditSectionLayout } from "./EditSectionLayout";
import { EditFooterBar } from "./EditFooterBar";
import { MembershipVerifyNotice } from "./MembershipVerifyNotice";
import { SectionAddBar } from "./SectionAddBar";
import { DetailCard } from "./DetailCard";
import { InsuranceFormModal } from "./InsuranceFormModal";
import { VerificationModal } from "./VerificationModal";
import { useInsuranceSection } from "./useInsuranceSection";
import { sectionRowKey, sectionStatus } from "./sectionDrafts";
import { INSURANCE_ROWS, INSURANCE_SPEC, insuranceRowValues } from "./insuranceFields";

/* Section 14 — Insurance.
 *
 * Transcribed from Insurance.jsx. Unlike Banking, this card DOES offer Edit —
 * the live one does, and the design draws the two buttons half and half.
 */

/* No insurance-specific path exists in uploadFilePath, so these share the
   company's authorised-letters folder, and `supporting_document` is the upload
   endpoint's own category for documents backing a section up. */
const VERIFY_SPEC = {
  uploadPath: uploadFilePath?.authLetters,
  category: "supporting_document",
};

const ROW_ICONS = {
  type: ShieldIcon,
  insurer: BriefcaseIcon,
  broker: UsersIcon,
};

export const InsuranceSection = ({
  onNext,
  onPrev,
  onSkip,
  onStatusChange,
  header,
  className = "",
}) => {
  const { rows, remove, refresh } = useInsuranceSection({ onStatusChange });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const openForm = (record) => {
    setEditing(record);
    setFormOpen(true);
  };

  const cardRows = (record) => {
    const values = insuranceRowValues(record);
    return INSURANCE_ROWS.map((row) => ({
      key: row.key,
      label: row.label,
      icon: ROW_ICONS[row.key],
      value: values[row.key],
    }));
  };

  return (
    <EditSectionLayout
      className={className}
      header={header}
      footer={<EditFooterBar onBack={onPrev} onSkip={onSkip} onContinue={onNext} />}
    >
      <h2 className="m-0 text-[16px] font-bold text-[var(--ink)] font-sans">
        Insurance
      </h2>

      {/* Members only — see MembershipVerifyNotice. The offer says the
          verification is free WITH YOUR MEMBERSHIP, so it is not shown to
          anyone who does not have one. */}
      <MembershipVerifyNotice onUpload={() => setVerifyOpen(true)} />

      <SectionAddBar
        className="mt-4"
        prompt="Add Insurance Information"
        label="Add Insurance"
        onAdd={() => openForm(null)}
      />

      {rows.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {rows.map((record) => (
            <DetailCard
              key={sectionRowKey(record, INSURANCE_SPEC)}
              status={sectionStatus(record)}
              rows={cardRows(record)}
              onEdit={() => openForm(record)}
              onDelete={() => remove(record)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <Modal modalHandle={closeForm} noClose bare>
          <InsuranceFormModal
            editing={editing}
            onClose={closeForm}
            onSaved={() => {
              closeForm();
              refresh();
            }}
          />
        </Modal>
      )}

      {verifyOpen && (
        <Modal modalHandle={() => setVerifyOpen(false)} noClose bare>
          <VerificationModal
            spec={VERIFY_SPEC}
            onClose={() => setVerifyOpen(false)}
            onDone={() => {
              setVerifyOpen(false);
              refresh();
            }}
          />
        </Modal>
      )}
    </EditSectionLayout>
  );
};

export default InsuranceSection;
